import { Component, NgZone } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { ActivatedRoute, Router, RouterModule } from '@angular/router'
import { LoaderService } from '../../../services/loader.service'
import { AgentBookingService } from '../../../services/agent-booking.service'
import { UserAuthService } from '../../../services/user-auth.service'
import { CustomBxInputComponent } from '../../../components/custom-bx-input/custom-bx-input.component'
import { AgentHeaderComponent } from '../../../components/agent-header/agent-header.component'
import { EMAIL_REGEXP } from '../../../common/validators/sharedRegex'
import { invalidDateValidator } from '../../../common/validators/custom-validators'
import { environment } from '../../../../environments/environment'

@Component({
    imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, CustomBxInputComponent, AgentHeaderComponent],
    selector: 'app-agent-tt-email',
    templateUrl: './agent-tt-email.component.html',
    styleUrls: ['./agent-tt-email.component.scss']
})
export class AgentTtEmailComponent {
  ttEmailForm!: FormGroup
  submitted = false
  consentChecked = false
  errorMessage = ''
  showUserNotFoundPopup = false
  emailErrorMessages = {
    required: () => 'Please enter an email address',
    pattern: () => 'Please enter a valid email address'
  }

  constructor (
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private loaderService: LoaderService,
    private agentBookingService: AgentBookingService,
    private userAuthService: UserAuthService,
    private ngZone: NgZone
  ) {}

  ngOnInit (): void {
    sessionStorage.removeItem('bookingType')

    this.ttEmailForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.pattern(EMAIL_REGEXP)]],
      dob: ['', [Validators.required, invalidDateValidator()]]
    })
  }

  get f () {
    return this.ttEmailForm.controls
  }

  onProceed (): void {
    this.submitted = true
    this.errorMessage = ''
    if (this.ttEmailForm.invalid || !this.consentChecked) {
      this.ttEmailForm.markAllAsTouched()
      return
    }

    const { email, dob } = this.ttEmailForm.value
    const formattedDob = this.formatDob(dob)

    this.loaderService.show()
    this.agentBookingService.searchUserProfile(email, formattedDob).subscribe({
      next: (res) => {
        this.loaderService.hide()
        sessionStorage.setItem('agentTtSub', res.sub)
        sessionStorage.setItem('agentTtUserProfileId', res.id)
        sessionStorage.setItem('agentTtEmail', email)
        sessionStorage.setItem('agentTtDob', formattedDob)
        this.userAuthService.setCookie('agentTtProfile', JSON.stringify(res))
        const queryParams = this.route.snapshot.queryParams
        const params = new URLSearchParams({ ...queryParams, agentFlow: 'true' }).toString()
        const redirectUrl = `${environment.bxURL}/account/agent-consent-upload?${params}`
        window.location.href = redirectUrl
      },
      error: (err) => {
        this.loaderService.hide()
        if (err.status === 404) {
          sessionStorage.setItem('agentTtEmail', email)
          sessionStorage.setItem('agentTtDob', formattedDob)
          this.showUserNotFoundPopup = true
        } else if (err.status === 400) {
          this.errorMessage = this.extractErrorMessage(err)
        } else {
          this.errorMessage = 'Something went wrong. Please try again.'
        }
      }
    })
  }

  onEmailBlur (): void {
    const emailControl = this.ttEmailForm.get('email')
    emailControl?.markAsTouched()
    emailControl?.updateValueAndValidity()
  }

  toggleConsent (): void {
    this.consentChecked = !this.consentChecked
  }

  goBack (): void {
    const queryParams = this.route.snapshot.queryParams
    const params = new URLSearchParams(queryParams).toString()
    const speakingUrl = params
      ? `${environment.bxsearchSpeakingUrl}?${params}`
      : environment.bxsearchSpeakingUrl
    window.location.href = speakingUrl
  }

  private formatDob (dob: any): string {
    if (typeof dob === 'string') {
      // Already in API format (YYYY-MM-DD)
      if (/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
        return dob
      }
      // DD/MM/YYYY string from manual typing -> convert to YYYY-MM-DD
      const parts = dob.split('/')
      if (parts.length === 3) {
        const [day, month, year] = parts
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
      }
      return ''
    }
    // NgbDateStruct format { year, month, day }
    if (dob && dob.year && dob.month && dob.day) {
      const month = String(dob.month).padStart(2, '0')
      const day = String(dob.day).padStart(2, '0')
      return `${dob.year}-${month}-${day}`
    }
    return ''
  }

  private extractErrorMessage (err: any): string {
    try {
      const body = err?.error
      if (body?.message) {
        const parsed = JSON.parse(body.message)
        if (parsed?.errors?.length) {
          return parsed.errors[0].message
        }
      }
    } catch (e) {
      // fallback
    }
    return 'The provided date of birth does not match the user profile.'
  }

  onCreateUser (): void {
    this.showUserNotFoundPopup = false
    this.ngZone.run(() => {
      this.router.navigate(['/agent/create-user'], { queryParams: this.route.snapshot.queryParams })
    })
  }

  onCancelPopup (): void {
    this.showUserNotFoundPopup = false
  }
}
